import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, User, Bell, LogOut, Home, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function DashboardLayout() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, []);

  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("dash.overview"), end: true },
    { to: "/dashboard/orders", icon: ShoppingBag, label: t("dash.orders") },
    { to: "/dashboard/notifications", icon: Bell, label: t("dash.notifications"), badge: unread },
    { to: "/dashboard/profile", icon: User, label: t("dash.profile") },
  ];

  const SidebarInner = () => (
    <>
      <div className="p-6"><Logo size="sm" /></div>
      <nav className="flex-1 px-3 space-y-1">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)} data-testid={`dash-nav-${it.to.split("/").pop() || "overview"}`}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-gradient-to-r from-amber-400/20 to-amber-600/5 text-amber-400 border border-amber-500/30" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <it.icon size={18} /> <span className="flex-1">{it.label}</span>
            {it.badge > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-amber-500 text-black text-xs font-bold flex items-center justify-center">{it.badge}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 space-y-1 border-t border-white/5">
        <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/5"><Home size={18} /> Back to Site</Link>
        <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-white/5" data-testid="dash-logout"><LogOut size={18} /> {t("nav.logout")}</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-amber-500/15 bg-[#0e1013] fixed inset-y-0">{SidebarInner()}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#0e1013] border-r border-amber-500/15 flex flex-col">{SidebarInner()}</div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="glass border-b border-amber-500/15 sticky top-0 z-30 flex items-center justify-between px-4 sm:px-6 h-16">
          <button className="lg:hidden text-slate-200" onClick={() => setOpen(!open)} data-testid="dash-mobile-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
          <div className="text-sm text-slate-400 hidden sm:block">{t("dash.welcome")}, <span className="text-white font-semibold">{user?.name}</span></div>
          <LanguageSwitcher compact />
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
